'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Timer, MessageSquare, Coffee, Bell, Megaphone } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { DemoTimer } from '../demo-types'

const TIMER_CONFIG: Record<string, {
  icon: typeof Timer
  color: string
  bgFrom: string
  bgTo: string
  borderColor: string
  label: string
  description: string
}> = {
  countdown: {
    icon: Timer,
    color: 'text-emerald-400',
    bgFrom: 'from-emerald-950/60',
    bgTo: 'to-green-950/60',
    borderColor: 'border-emerald-500/40',
    label: 'Timer',
    description: 'Countdown in progress',
  },
  request_period: {
    icon: MessageSquare,
    color: 'text-violet-400',
    bgFrom: 'from-violet-950/60',
    bgTo: 'to-purple-950/60',
    borderColor: 'border-violet-500/40',
    label: 'Request Period',
    description: 'Send your song requests now!',
  },
  break_reminder: {
    icon: Coffee,
    color: 'text-amber-400',
    bgFrom: 'from-amber-950/60',
    bgTo: 'to-orange-950/60',
    borderColor: 'border-amber-500/40',
    label: 'Break Coming',
    description: 'Short break coming up',
  },
  announcement: {
    icon: Megaphone,
    color: 'text-pink-400',
    bgFrom: 'from-pink-950/60',
    bgTo: 'to-rose-950/60',
    borderColor: 'border-pink-500/40',
    label: 'Announcement',
    description: 'Stay tuned!',
  },
  custom: {
    icon: Bell,
    color: 'text-cyan-400',
    bgFrom: 'from-cyan-950/60',
    bgTo: 'to-teal-950/60',
    borderColor: 'border-cyan-500/40',
    label: 'Timer',
    description: 'Countdown in progress',
  },
}

interface TimerBannerProps {
  timer: DemoTimer
}

export function TimerBanner({ timer }: TimerBannerProps) {
  const t = useTranslations('demo')

  const TIMER_LABELS: Record<string, { label: string; description: string }> = {
    countdown: { label: t('timerLabel'), description: t('countdownInProgress') },
    request_period: { label: t('requestPeriod'), description: t('sendRequestsNow') },
    break_reminder: { label: t('breakComing'), description: t('shortBreakComing') },
    announcement: { label: t('announcement'), description: t('stayTuned') },
    custom: { label: t('timerLabel'), description: t('countdownInProgress') },
  }

  // Calculate remaining time locally for smooth countdown
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    const now = new Date().getTime()
    const endsAt = new Date(timer.endsAt).getTime()
    return Math.max(0, Math.round((endsAt - now) / 1000))
  })

  useEffect(() => {
    // Update remaining time every second based on endsAt timestamp
    const interval = setInterval(() => {
      const now = new Date().getTime()
      const endsAt = new Date(timer.endsAt).getTime()
      const remaining = Math.max(0, Math.round((endsAt - now) / 1000))
      setRemainingSeconds(remaining)
    }, 1000)

    return () => clearInterval(interval)
  }, [timer.endsAt])

  // Re-sync when timer changes (e.g., new timer started)
  useEffect(() => {
    const now = new Date().getTime()
    const endsAt = new Date(timer.endsAt).getTime()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing countdown with timer data changes
    setRemainingSeconds(Math.max(0, Math.round((endsAt - now) / 1000)))
  }, [timer.id, timer.endsAt])

  const config = TIMER_CONFIG[timer.type] || TIMER_CONFIG.countdown
  const Icon = config.icon

  // Format remaining time as MM:SS
  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`

  const isLowTime = remainingSeconds <= 60 && remainingSeconds > 0
  const isExpired = remainingSeconds <= 0

  if (isExpired) return null

  return (
    <Card className={`${config.borderColor} bg-gradient-to-r ${config.bgFrom} ${config.bgTo}`}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-full ${config.color.replace('text-', 'bg-').replace('-400', '-500/20')}`}>
              <Icon className={`h-5 w-5 ${config.color}`} />
            </div>
            <div>
              <p className="font-semibold text-white">
                {timer.label || (TIMER_LABELS[timer.type] || TIMER_LABELS.countdown).label}
              </p>
              <p className={`text-sm ${config.color.replace('-400', '-200/80')}`}>
                {(TIMER_LABELS[timer.type] || TIMER_LABELS.countdown).description}
              </p>
            </div>
          </div>
          <div className={`text-3xl font-mono font-bold tabular-nums ${
            isLowTime ? 'text-red-400 animate-pulse' : config.color
          }`}>
            {timeDisplay}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
